/**
 * usePersistentNumber — a number state persisted to localStorage across reloads.
 *
 * Modeled on `useCollapsibleSection` (content-plan): namespaced key, SSR-safe
 * (`typeof window` gated), and private-mode / disabled-storage safe (falls back
 * to in-memory state without throwing). Optional clamping to [min, max].
 *
 * Used for the screen-grid preview scale slider.
 */

import { useCallback, useState } from 'react';

const KEY_PREFIX = 'seg:num:';

function clampValue(value: number, min?: number, max?: number): number {
  let next = value;
  if (typeof min === 'number') next = Math.max(min, next);
  if (typeof max === 'number') next = Math.min(max, next);
  return next;
}

function readStored(key: string, fallback: number, min?: number, max?: number): number {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(`${KEY_PREFIX}${key}`);
    if (raw === null) return fallback;
    const parsed = Number(raw);
    if (!Number.isFinite(parsed)) return fallback;
    return clampValue(parsed, min, max);
  } catch {
    return fallback;
  }
}

function writeStored(key: string, value: number): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${KEY_PREFIX}${key}`, String(value));
  } catch {
    /* storage disabled — silently no-op */
  }
}

export interface UsePersistentNumberOptions {
  min?: number;
  max?: number;
}

export function usePersistentNumber(
  key: string,
  defaultValue: number,
  options: UsePersistentNumberOptions = {},
): [number, (value: number) => void] {
  const { min, max } = options;
  const [value, setValueState] = useState<number>(() =>
    readStored(key, defaultValue, min, max),
  );

  const setValue = useCallback(
    (next: number) => {
      const clamped = clampValue(next, min, max);
      setValueState(clamped);
      writeStored(key, clamped);
    },
    [key, min, max],
  );

  return [value, setValue];
}
